import getConfig from "@/utils/getConfig";
import { GetOverwolfUserStatsRewardCategoryEnum, OWUserStat, Token, DevourPlayApi } from "@devour/client";
import {useQuery} from "@tanstack/react-query";

interface GetDevourPlayUserStatsResponse {
    message: string;
    userStats: OWUserStat[];
    uniqueGameIds: string[];
    /**
     * Weekly XP cap for time-played rewards (platform-wide OWReward.rewardMax
     * for PLAYTIME). Omitted when the backend has no PLAYTIME reward record.
     * Consumers MUST NOT default to 0 — render the count without a denominator
     * when this field is absent.
     */
    timeRewardWeeklyMax?: number;
    pagination?: {
        page: number;
        pageSize: number;
        total: number;
        totalPages: number;
    };
}

async function fetchDevourPlayUserStats(
    fullToken: Token,
    gameId?: string,
    rewardCategory?: GetOverwolfUserStatsRewardCategoryEnum,
    page?: number,
    pageSize?: number,
    daysBack?: number,
): Promise<GetDevourPlayUserStatsResponse> {
    const params: any = {};

    // Only add parameters if they're actually provided (not undefined)
    if (page !== undefined) {
        params.page = page;
    }
    if (pageSize !== undefined) {
        params.pageSize = pageSize;
    }
    if (daysBack !== undefined) {
        params.daysBack = daysBack;
    }
    if (gameId) {
        params.gameId = gameId;
    }
    if (rewardCategory) {
        params.rewardCategory = rewardCategory;
    }

    const data = await new DevourPlayApi(getConfig(fullToken)).getDevourplayUserQuestStats({
        gameId,
        rewardCategory: rewardCategory as any,
        page,
        pageSize,
        daysBack,
    });
    return data as unknown as GetDevourPlayUserStatsResponse;
}

function useGetDevourPlayUserStatsQuery(
    fullToken: Token,
    gameId?: string,
    enabled?: boolean,
    category?: GetOverwolfUserStatsRewardCategoryEnum,
    page?: number,
    pageSize?: number,
    daysBack?: number,
) {
    return {
        queryKey: [
            "devourplay-user-quest-stats",
            fullToken?.id ?? "unauthenticated",
            gameId || "all-games",
            category || "all-categories",
            page,
            pageSize,
            daysBack,
        ],
        queryFn: () => fullToken ? fetchDevourPlayUserStats(fullToken, gameId, category, page, pageSize, daysBack) : null,
        staleTime: 10 * 1000, // Consider data fresh for 10 seconds - more responsive to updates
        gcTime: 2 * 60 * 1000, // Keep in cache for 2 minutes (was cacheTime: 0)
        enabled: enabled && !!fullToken, // Allow queries without gameId to get all stats
        refetchOnWindowFocus: true, // Refetch when tab gains focus to catch external updates
        refetchOnReconnect: "always", // Always refetch on reconnection
    } as const;
}

export default function useGetDevourPlayUserStats(
    fullToken: Token,
    gameId?: string,
    enabled: boolean = true,
    category?: GetOverwolfUserStatsRewardCategoryEnum,
    page?: number,
    pageSize?: number,
    daysBack?: number,
) {
    return useQuery<GetDevourPlayUserStatsResponse>(useGetDevourPlayUserStatsQuery(fullToken, gameId, enabled, category, page, pageSize, daysBack));
}